// Story 10.1 — `useMe`: lookup del usuario autenticado vía cookie.
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '@/lib/api-client';
import type { Capability, OrganizationRole, UserRole } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-client';

/** Story 16.1 — una organización del usuario y su rol en ella. */
export interface OrganizationMembership {
  id: number;
  name: string;
  slug: string;
  role: OrganizationRole;
}

export interface AuthUser {
  id: number;
  email: string;
  name: string | null;
  // Rol de PLATAFORMA: `admin` (staff, todo), `support` (staff, solo lectura),
  // `member` (cuenta de cliente; su acceso sale de sus organizaciones).
  role: UserRole;
  organizations: OrganizationMembership[];
  /**
   * Story 16.2 — capabilities efectivas en alguna organización (o como staff).
   * Para lo de un bot concreto se usan las del bot (`BotRowSummary.capabilities`).
   */
  capabilities: Capability[];
}

/**
 * El backend retorna `{ data: { user: AuthUser } }`. El api-client unwrappea
 * el `data`, asi que aqui obtenemos `{ user }` y desempaquetamos el último
 * nivel para que los consumers (`useUserRole`, `<Can>`) accedan a los campos
 * directamente. Sin esto, `data?.role` es undefined → role queda null →
 * todas las capability checks fallan.
 */
interface MeEnvelope {
  user: Omit<AuthUser, 'organizations' | 'capabilities'>;
  organizations?: OrganizationMembership[];
  capabilities?: Capability[];
}

/**
 * `GET /auth/me` → `AuthUser`, o `null` si no hay sesión (401). Lo comparten
 * `useMe` y el guard del router: usan la misma query key, así que tienen que
 * devolver la misma forma (antes el router cacheaba solo `user`).
 */
export async function fetchMe(): Promise<AuthUser | null> {
  try {
    const env = await api.get<MeEnvelope>('/api/v1/auth/me');
    return {
      ...env.user,
      organizations: env.organizations ?? [],
      capabilities: env.capabilities ?? [],
    };
  } catch (err) {
    // 401 → no autenticado. El router gate lo maneja.
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: fetchMe,
    // P-324: 1 retry para errores transitorios (5xx / network) — sin
    // esto, un 502 momentáneo del API hace `data: undefined` → router
    // guard cree que no hay sesión → patea al user a /login innecesario.
    // 401 NO se retrintenta (ya está handled como `null` arriba).
    retry: (n, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return n < 1;
    },
    staleTime: 60_000,
  });
}
